import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { useHoofdstuk } from '@/api/queries/objects';

import { useViewerBesluitFilterGrondslagen } from './-utils/useViewerBesluitFilterGrondslagen';
import { useViewerBesluitGrondslagen } from './-utils/useViewerBesluitGrondslagen';

/**
 * Search params
 */
const searchSchema = z.object({
  // voorschrift can be used to redirect to the hoofdstuk page of the voorschrift
  voorschrift: z.string().optional(),
  labelIds: z.array(z.string().uuid()).optional().catch([]),
  view: z.enum(['inhoud', 'labels']),
});

export const Route = createFileRoute(
  '/$bedrijfLineageId/viewer/besluit/$besluitId/_layout/_inhoud/redirect'
)({
  validateSearch: searchSchema.parse,
  beforeLoad: async ({ context: { queryClient }, search, params }) => {
    /**
     * When a voorschrift is passed, we redirect to the besluit and hoofdstuk where it is created
     */
    if (search.voorschrift) {
      const hoofdstuk = await queryClient.ensureQueryData(
        useHoofdstuk.getOptions({
          page: 1,
          size: 1,
          filter: [
            {
              model: 'VSPB',
              field: 'Voorschrift',
              op: 'eq',
              value: search.voorschrift,
            },
            {
              model: 'VSPB',
              field: 'Type',
              op: 'eq',
              value: 'Eigen',
            },
          ],
        })
      );

      const hoofdstukId = hoofdstuk.objects?.[0]?.ID;

      if (hoofdstukId) {
        throw redirect({
          to: '/$bedrijfLineageId/viewer/besluit/$besluitId/voorschriften/$hoofdstukId',
          params: {
            bedrijfLineageId: params.bedrijfLineageId,
            besluitId: params.besluitId,
            hoofdstukId,
          },
        });
      }
    }

    /**
     * When labels are passed, we try to redirect to the first hoofdstuk with the labels
     */
    if (search.view === 'labels' && search.labelIds?.length) {
      const grondslagen = await queryClient.ensureQueryData(
        useViewerBesluitFilterGrondslagen.getOptions({
          besluitId: params.besluitId,
          labelIds: search.labelIds,
        })
      );

      const firstHoofdstuk = grondslagen?.[0]?.hoofdstukken[0];

      /**
       * If there's no first hoofdstuk, redirect to the besluittekst
       */
      if (!firstHoofdstuk) {
        throw redirect({
          to: '/$bedrijfLineageId/viewer/besluit/$besluitId/besluittekst',
          params,
          search: { labelIds: search.labelIds },
        });
      }

      throw redirect({
        to: '/$bedrijfLineageId/viewer/besluit/$besluitId/labels/$hoofdstukId',
        params: {
          bedrijfLineageId: params.bedrijfLineageId,
          besluitId: params.besluitId,
          hoofdstukId: firstHoofdstuk.ID,
        },
        search: { labelIds: search.labelIds },
      });
    }

    /**
     * By default, we try to redirect to the first hoofdstuk
     */
    const { grondslagen, lineageIdsWithVoorschriften } =
      await queryClient.ensureQueryData(
        useViewerBesluitGrondslagen.getOptions({
          besluitId: params.besluitId,
        })
      );
    const firstHoofdstuk = grondslagen?.[0]?.hoofdstukken[0];

    if (firstHoofdstuk?.ID) {
      // If the first hoofdstuk has a voorschriften page, we redirect to that page
      if (lineageIdsWithVoorschriften.has(firstHoofdstuk.Lineage)) {
        throw redirect({
          to: '/$bedrijfLineageId/viewer/besluit/$besluitId/voorschriften/$hoofdstukId',
          params: {
            bedrijfLineageId: params.bedrijfLineageId,
            besluitId: params.besluitId,
            hoofdstukId: firstHoofdstuk.ID,
          },
        });
      }

      // If it doesn't have a voorschriften page, we redirect to the inhoudelijke overwegingen page
      throw redirect({
        to: '/$bedrijfLineageId/viewer/besluit/$besluitId/inhoudelijke-overwegingen/$hoofdstukId',
        params: {
          bedrijfLineageId: params.bedrijfLineageId,
          besluitId: params.besluitId,
          hoofdstukId: firstHoofdstuk.ID,
        },
      });
    }

    /**
     * If there's no first hoofdstuk, redirect to the besluittekst
     */
    throw redirect({
      to: '/$bedrijfLineageId/viewer/besluit/$besluitId/besluittekst',
      params,
    });
  },
});
